import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { isWithinInterval, addDays, format } from 'date-fns';
import { parseLocalDate, isOverdue } from '../utils/dates';
import { useApp } from '../context/AppContext';
import TaskCard from '../components/tasks/TaskCard';

const Dashboard = () => {
  const {
    tasks, projects, expenses, appliances, lowStockCount,
    fetchDashboard, loading,
  } = useApp();

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const now = new Date();
  const overdue = tasks.filter((t) => isOverdue(parseLocalDate(t.nextDue)));
  const next7 = tasks.filter((t) => {
    const due = parseLocalDate(t.nextDue);
    return !isOverdue(due) && isWithinInterval(due, { start: now, end: addDays(now, 7) });
  });
  const next30 = tasks.filter((t) => {
    const due = parseLocalDate(t.nextDue);
    return !isOverdue(due) && isWithinInterval(due, { start: addDays(now, 7), end: addDays(now, 30) });
  });

  const activeProjects = projects.filter((p) => p.status === 'in-progress');
  const highPriority = projects.filter((p) => p.priority === 'high' && p.status !== 'completed');
  const completedProjects = projects.filter((p) => p.status === 'completed');
  const roiDiySpent = completedProjects.reduce((s, p) => s + (p.actualCost ?? p.estimatedCost ?? 0), 0);
  const roiProEquivalent = completedProjects.reduce((s, p) => s + (p.professionalCost ?? 0), 0);
  const roiValueAdded = completedProjects.reduce((s, p) => s + (p.estimatedValueAdd ?? 0), 0);
  const roiSavings = roiProEquivalent - roiDiySpent;
  const showRoi = completedProjects.length > 0 && (roiProEquivalent > 0 || roiValueAdded > 0);

  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const thisMonth = expenses.filter((e) => new Date(e.date) >= monthStart)
    .reduce((s, e) => s + e.amount, 0);

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div className="page">
      <div className="dash-hero">
        <div>
          <h1>Home Dashboard</h1>
          <p className="page-subtitle">{format(now, 'EEEE, MMMM d, yyyy')}</p>
        </div>
        <div className="dash-quick-actions">
          <Link to="/tasks" className="btn btn-primary btn-sm">+ Task</Link>
          <Link to="/projects/intake" className="btn btn-secondary btn-sm">✨ AI Project</Link>
          <Link to="/calendar" className="btn btn-secondary btn-sm">📅 Calendar</Link>
        </div>
      </div>

      {/* Stats row */}
      <div className="stats-row">
        <a href="#overdue-section" className="stat-card stat-danger" style={{ textDecoration: 'none', color: 'inherit' }}>
          <span className="stat-number">{overdue.length}</span>
          <span className="stat-label">Overdue Tasks</span>
        </a>
        <a href="#this-week-section" className="stat-card stat-warning" style={{ textDecoration: 'none', color: 'inherit' }}>
          <span className="stat-number">{next7.length}</span>
          <span className="stat-label">Due This Week</span>
        </a>
        <Link to="/projects" className="stat-card stat-info" style={{ textDecoration: 'none' }}>
          <span className="stat-number">{activeProjects.length}</span>
          <span className="stat-label">Active Projects</span>
        </Link>
        <Link to="/appliances" className="stat-card stat-primary" style={{ textDecoration: 'none' }}>
          <span className="stat-number">{appliances.length}</span>
          <span className="stat-label">Appliances</span>
        </Link>
        <Link to="/expenses" className="stat-card stat-success" style={{ textDecoration: 'none' }}>
          <span className="stat-number">${thisMonth.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
          <span className="stat-label">Spent This Month</span>
        </Link>
<Link to="/consumables" className={`stat-card${lowStockCount > 0 ? ' stat-danger' : ''}`} style={{ textDecoration: 'none' }}>
          <span className="stat-number">{lowStockCount}</span>
          <span className="stat-label">Low Stock Items</span>
        </Link>
      </div>

      {/* Overdue tasks */}
      {overdue.length > 0 && (
        <section id="overdue-section">
          <h2>⚠ Overdue Tasks</h2>
          <div className="card-grid">
            {overdue.map((task) => <TaskCard key={task._id} task={task} onEdit={() => {}} />)}
          </div>
        </section>
      )}

      {/* Due this week */}
      {next7.length > 0 && (
        <section id="this-week-section">
          <h2>This Week</h2>
          <div className="card-grid">
            {next7.map((task) => <TaskCard key={task._id} task={task} onEdit={() => {}} />)}
          </div>
        </section>
      )}

      {/* Due next 30 days (after this week) */}
      {next30.length > 0 && (
        <section>
          <h2>Coming Up (Next 30 Days)</h2>
          <div className="card-grid">
            {next30.map((task) => <TaskCard key={task._id} task={task} onEdit={() => {}} />)}
          </div>
        </section>
      )}

      {/* High-priority projects */}
      {highPriority.length > 0 && (
        <section>
          <div className="section-header">
            <h2>High-Priority Projects</h2>
            <Link to="/projects" className="btn btn-secondary btn-sm">View All</Link>
          </div>
          <div className="dash-project-list">
            {highPriority.map((p) => (
              <div key={p._id} className="dash-project-row">
                <div>
                  <span className="dash-project-title">{p.title}</span>
                  <span className={`badge badge-status-${p.status}`} style={{ marginLeft: '0.5rem' }}>{p.status.replace('-', ' ')}</span>
                </div>
                <div className="dash-project-meta">
                  {p.estimatedCost != null && <span>${p.estimatedCost.toLocaleString()}</span>}
                  {p.targetDate && <span>Target: {format(new Date(p.targetDate), 'MMM d, yyyy')}</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Investment summary */}
      {showRoi && (
        <section>
          <div className="section-header">
            <h2>Investment Summary</h2>
            <Link to="/projects?status=completed" className="btn btn-secondary btn-sm">View Completed</Link>
          </div>
          <div className="roi-card">
            <div className="roi-grid">
              <div className="roi-metric">
                <span className="roi-value">${roiDiySpent.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                <span className="roi-label">DIY Spent</span>
              </div>
              {roiProEquivalent > 0 && (
                <div className="roi-metric">
                  <span className="roi-value">${roiProEquivalent.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  <span className="roi-label">Pro Equivalent</span>
                </div>
              )}
              {roiProEquivalent > 0 && (
                <div className="roi-metric roi-metric-highlight">
                  <span className="roi-value">${roiSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  <span className="roi-label">DIY Savings</span>
                </div>
              )}
              {roiValueAdded > 0 && (
                <div className="roi-metric">
                  <span className="roi-value">+${roiValueAdded.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  <span className="roi-label">Est. Value Added</span>
                </div>
              )}
            </div>
            <p className="roi-footnote">Across {completedProjects.length} completed project{completedProjects.length !== 1 ? 's' : ''}</p>
          </div>
        </section>
      )}

      {overdue.length === 0 && next7.length === 0 && next30.length === 0 && (
        <div className="empty-state-card">
          <p style={{ fontSize: '2rem' }}>✅</p>
          <p>All caught up! No upcoming tasks in the next 30 days.</p>
          <Link to="/tasks" className="btn btn-primary" style={{ marginTop: '1rem' }}>Manage Tasks</Link>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
